<template>
  <div class="iv-location-select">
    <div class="type-select-container">
      <label>Types</label>
      <md-field class="type-select">
        <md-select
          :value="ivLocation.categoryTypes"
          :multiple="true"
          placeholder="Any type"
          :disabled="readonly || !availableCategories.length"
          @input="typeKeys => updateTypes(typeKeys)"
        >
          <md-option
            v-for="category in availableCategories"
            :key="category.key"
            :value="category.key"
          >
            {{ category.label.en }}
          </md-option>
        </md-select>
      </md-field>
      <help-tooltip v-if="ivCategoryError" type="error">
        <span>{{ ivCategoryError.message }}</span>
      </help-tooltip>
    </div>

    <div class="port-select-container">
      <label>Ports</label>
      <md-field class="port-select">
        <md-select
          :value="ivLocation.ports"
          :multiple="true"
          placeholder="Any port"
          :disabled="readonly || !availablePorts.length"
          @input="portKeys => updatePorts(portKeys)"
        >
          <md-option
            v-for="port in availablePorts"
            :key="port.key"
            :value="port.key"
          >
            {{ port.label.en }}
          </md-option>
        </md-select>
      </md-field>
      <help-tooltip v-if="ivPortError" type="error">
        <span>{{ ivPortError.message }}</span>
      </help-tooltip>
    </div>

    <div class="location-select-container">
      <label>Locations</label>
      <md-field class="site-select">
        <md-select
          :value="ivLocation.sites"
          :multiple="true"
          placeholder="Any location"
          :disabled="readonly || !availableSites.length"
          @input="siteKeys => updateSites(siteKeys)"
        >
          <md-option
            v-for="site in availableSites"
            :key="site.key"
            :value="site.key"
          >
            {{ site.label.en }}
          </md-option>
        </md-select>
      </md-field>
      <help-tooltip v-if="ivSiteError" type="error">
        <span>{{ ivSiteError.message }}</span>
      </help-tooltip>
    </div>
    <div class="side-select-container">
      <label>Sides</label>
      <md-field class="side-select">
        <md-select
          :value="ivLocation.sides"
          :multiple="true"
          placeholder="Any side"
          :disabled="readonly || !availableSides.length"
          @input="sideKeys => updateSides(sideKeys)"
        >
          <md-option
            v-for="side in availableSides"
            :key="side.key"
            :value="side.key"
          >
            {{ side.label.en }}
          </md-option>
        </md-select>
      </md-field>
      <help-tooltip v-if="ivSideError" type="error">
        <span>{{ ivSideError.message }}</span>
      </help-tooltip>
    </div>
  </div>
</template>

<script>
import HelpTooltip from '~lib/common/components/HelpTooltip';

import iv from '~lib/common/config/iv';

import {
  getAvailableIvCategories,
  getAvailableIvSites,
  getAvailableIvPorts,
  getAvailableIvSides
} from '~lib/common/utils/availableIvOptions';

export default {
  name: 'iv-location-select',
  components: {
    HelpTooltip,
  },
  props: {
    ivLocation: {
      type: Object,
      required: true,
    },
    caseConf: {
      type: Object,
      required: true,
    },
    isGlobal: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    ivCategoryError: {
      type: Object,
      default: () => null,
    },
    ivPortError: {
      type: Object,
      default: () => null,
    },
    ivSiteError: {
      type: Object,
      default: () => null,
    },
    ivSideError: {
      type: Object,
      default: () => null,
    },
  },
  computed: {
    productType() {
      if (this.$featureAvailabilityService) {
        return this.$featureAvailabilityService.productType()
      }
      return 'EMS';
    },
    availableCategories() {
      if (this.isGlobal) {
        return iv.categories.concat(iv.oasCategories);
      }
      if (!this.caseConf.iv) {
        return [];
      }
      return getAvailableIvCategories(this.caseConf, this.ivLocation.categoryTypes);
    },
    availablePorts() {
      if (this.isGlobal) {
        return iv.portNames;
      }
      if (!this.caseConf.iv) {
        return [];
      }

      return getAvailableIvPorts(this.caseConf, this.ivLocation.ports);
    },
    availableSites() {
      if (this.isGlobal) {
        return iv.sites;
      }
      if (!this.caseConf.iv) {
        return [];
      }
      return getAvailableIvSites(this.caseConf, this.ivLocation.sites, this.ivLocation.categoryTypes);
    },
    availableSides() {
      if (this.isGlobal) {
        return iv.sides;
      }
      return getAvailableIvSides(this.caseConf, this.ivLocation.sides);
    },
    oasPlacements() {
      return iv.oasPlacements.filter(
        placement => {
          return placement.productTypes.includes(this.productType)
        }
      );
    },
    oasPorts() {
      return iv.portNames.filter(
        portName => this.oasPlacements.some(placement => placement.portNames.includes(portName.key))
      );
    },
    oasSites() {
      return iv.sites.filter(
        site => this.oasPlacements.some(
          placement => placement.sites.find(placementSite => placementSite.site === site.key)
        )
      );
    },
  },
  methods: {
    updateTypes(typeKeys) {
      this.$emit('update', {
        ...this.ivLocation,
        categoryTypes: typeKeys,
      });
    },
    updatePorts(portKeys) {
      this.$emit('update', {
        ...this.ivLocation,
        ports: portKeys,
      });
    },
    updateSites(siteKeys) {
      this.$emit('update', {
        ...this.ivLocation,
        sites: siteKeys,
      });
    },
    updateSides(sideKeys) {
      this.$emit('update', {
        ...this.ivLocation,
        sides: sideKeys,
      });
    },
  },
};
</script>

<style scoped>
.iv-location-select {
  display: flex;
  flex-direction: row;
  padding-top: 0;
}
.other-options-container {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex-wrap: wrap;
  padding-top: 0;
}

label {
  display: none;
}

.type-select-container,
.port-select-container,
.location-select-container,
.side-select-container {
  display: flex;
}

.help-tooltip {
  margin-top: -18px;
  margin-left: -6px;
}
</style>
